import { Helmet } from "react-helmet"
import { i18n } from "@lingui/core"
import { t } from "@lingui/macro"
import { useProductStore } from "../../provider/ProductStoreProvider"

const CURRENCY_BY_LOCALE: Record<string, string> = {
  "es-AR": "ARS",
  "es-MX": "MXN",
  "pt-BR": "BRL",
}

export function ProductVIPHead() {
  const canonicalHref = window.location.href.split("?")[0]

  const { product, selectedVariant } = useProductStore()

  const headTitle = product.title
    ? t`${product.title} - Comprar en ${window.STORE_NAME}`
    : t`Comprar en ${window.STORE_NAME}`
  const descriptionInfo = product.description ? product.description : ""
  const ogImage = product.images[0]?.thumbnails.large

  const activeVariant =
    selectedVariant ??
    product.variants.find(v => v.stock > 0) ??
    product.variants[0]
  const price = activeVariant?.price / 100
  const currency =
    activeVariant?.currency ?? CURRENCY_BY_LOCALE[i18n.locale] ?? "ARS"
  const isInStock = activeVariant ? activeVariant.stock > 0 : false
  const availability = isInStock
    ? "https://schema.org/InStock"
    : "https://schema.org/OutOfStock"

  const productSchema =
    product.title && price != null
      ? {
          "@context": "https://schema.org",
          "@type": "Product",
          name: product.title,
          description: descriptionInfo || undefined,
          image: product.images.map(img => img.thumbnails.large),
          sku: product.identifier || undefined,
          brand: { "@type": "Brand", name: window.STORE_NAME },
          offers: {
            "@type": "Offer",
            priceCurrency: currency,
            price,
            availability,
            url: canonicalHref,
          },
        }
      : null

  return (
    <Helmet>
      <title>{headTitle}</title>
      <link rel="canonical" href={canonicalHref} />
      <meta name="description" content={descriptionInfo} />
      <meta property="og:site_name" content={window.STORE_NAME} />
      <meta property="og:type" content="product" />
      <meta property="og:title" content={headTitle} />
      <meta property="og:description" content={descriptionInfo} />
      <meta property="og:url" content={canonicalHref} />
      {ogImage && <meta property="og:image" content={ogImage} />}
      {price != null && (
        <meta property="product:price:amount" content={String(price)} />
      )}
      {price != null && (
        <meta property="product:price:currency" content={currency} />
      )}
      <meta
        property="product:availability"
        content={isInStock ? "in stock" : "out of stock"}
      />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={headTitle} />
      <meta name="twitter:description" content={descriptionInfo} />
      {ogImage && <meta name="twitter:image" content={ogImage} />}
      {ogImage && <link rel="preload" as="image" href={ogImage} />}
      {productSchema && (
        <script type="application/ld+json">
          {JSON.stringify(productSchema)}
        </script>
      )}
    </Helmet>
  )
}
